import React from 'react'
import clsx from 'clsx'
import moment from 'moment'

import { format as formatDate, isValid as dateIsValid } from 'date-fns'
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers'

import OverflowTooltip from '../../../atoms/OverflowTooltip'
import FieldCopy from '../FieldCopy'

import LightTheme from '../../../../themes/LightTheme'
import useStyles from '../styles'

const getDateValue = (value, type) => {
  try {
    if (type === 'date-time') {
      return value ? new Date(value) : new Date()
    }
    if (typeof value === 'string' && value.includes('Z')) {
      const dateWithoutTime = value.split('T')[0]
      return moment(dateWithoutTime).startOf('day').toDate()
    }
    return value ? new Date(value) : new Date()
  } catch (error) {
    console.error('Error parsing date on custom field!!!', value, type, error)
    return new Date()
  }
}

export const formatDateAsString = ({ type, date }) => {
  switch (type) {
    case 'month-year':
      return formatDate(date, 'MMMM yyyy')
    case 'date-time':
      return formatDate(date, 'MMMM do yyyy, h:mm a')
    case 'date-only':
    default:
      return formatDate(date, 'MMMM do, yyyy')
  }
}

export const formatVal = ({
  type,
  value,
  inputOnly = false,
  fallback = ''
}) => {
  if (!value) {
    return (
      <span
        style={{ color: inputOnly ? 'black' : LightTheme.palette.textLight }}
      >
        {fallback ? fallback : 'Select'}
      </span>
    )
  }

  const date = getDateValue(value, type)
  if (!dateIsValid(date)) {
    return (
      <span
        style={{ color: inputOnly ? 'black' : LightTheme.palette.textLight }}
      >
        {fallback ? fallback : 'Select'}
      </span>
    )
  }

  return formatDateAsString({ type, date })
}

const getTextValue = ({ type, value }) => {
  if (!value) {
    return ''
  }

  const date = getDateValue(value, type)
  if (!dateIsValid(date)) {
    return ''
  }

  return formatDateAsString({ type, date })
}

const OPEN_STATE = {
  NEVER: 0,
  OPEN: 1,
  CLOSED: 2
}

const DateInput = ({
  field,
  value,
  disabled,
  canEdit,
  handleChange,
  handleBlur,
  timelineMode,
  task,
  inputOnly,
  row,
  rowKey,
  parent,
  inTable
}) => {
  const holderRef = React.useRef()

  // Used for auto save
  const [startingValue, setStartingValue] = React.useState('')
  const [openState, setOpenState] = React.useState(OPEN_STATE.NEVER)
  const [hovering, setHovering] = React.useState(false)

  // Select picker based on field type
  const PickerElement = React.useMemo(() => {
    if (field.field_type === 'date-time') {
      return DateTimePicker
    }

    return DatePicker
  }, [field.field_type])

  // Props to control opening view and behaviour of picker
  const pickerProps = React.useMemo(() => {
    const type = field.field_type

    if (type === 'month-year') {
      return {
        openTo: 'month',
        views: ['month', 'year']
      }
    }

    if (type === 'date-time') {
      return {
        openTo: 'day',
        timeSteps: { minutes: 1 },
        views: ['day', 'month', 'year', 'hours', 'minutes']
      }
    }

    return {
      openTo: 'day',
      views: ['day', 'month', 'year']
    }
  }, [field.field_type])

  const classes = useStyles({
    readOnly: disabled,
    canEdit,
    dateTimelineMode: timelineMode,
    task,
    inputOnly,
    inTable
  })

  const handleOnDateChange = value => {
    let dateToSave
    if (field.field_type === 'date-time') {
      dateToSave = value.toDate()
    } else {
      //dates are pain
      //set time to start of day.
      const selectedDate = value.toDate()
      dateToSave = new Date(
        Date.UTC(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate()
        )
      ).toISOString()
    }

    handleChange({ target: { value: dateToSave } }, field, row, rowKey, parent)
  }

  const getValue = () => {
    if (field.field_type === 'date-time') {
      return value ? moment(value) : moment()
    }

    if (typeof value === 'string' && value.includes('Z')) {
      const dateWithoutTime = value.split('T')[0]
      return moment(dateWithoutTime).startOf('day')
    } else if (value) {
      const toIso = moment(value).toISOString()
      const dateWithoutTime = toIso.split('T')[0]
      return moment(dateWithoutTime).startOf('day')
    }
    return moment()
  }

  // Saves when picker is closed with changes
  React.useEffect(() => {
    if (openState !== OPEN_STATE.CLOSED) {
      return
    }

    // Check that value was changed since last save
    const newVal = formatVal({ type: field.field_type, value, inputOnly })
    if (newVal !== startingValue) {
      handleBlur && handleBlur()
    }
  }, [openState, field.field_type, value, startingValue, handleBlur, inputOnly])

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={clsx(classes.fieldInputRoot, 'cf-input-root')}
    >
      {!inputOnly && (
        <OverflowTooltip
          text={field.description}
          className={clsx(classes.label, 'label')}
        >
          {field.title}
        </OverflowTooltip>
      )}

      <FieldCopy
        inTable={inTable}
        visible={hovering}
        text={getTextValue({ type: field.field_type, value })}
      />

      <div ref={holderRef} style={{ width: '100%' }}>
        {openState === OPEN_STATE.OPEN ? (
          <PickerElement
            open
            closeOnSelect
            value={getValue()}
            slots={{
              field: () => (
                <div className={classes.date}>
                  {formatVal({ type: field.field_type, value, inputOnly })}
                </div>
              )
            }}
            slotProps={{
              popper: {
                anchorEl: holderRef?.current
              },
              actionBar: { actions: ['today', 'accept', 'cancel'] }
            }}
            onChange={handleOnDateChange}
            disabled={disabled || !canEdit}
            onClose={() => setOpenState(OPEN_STATE.CLOSED)}
            {...pickerProps}
          />
        ) : (
          <div
            onClick={() => {
              setStartingValue(
                formatVal({ type: field.field_type, value, inputOnly })
              )
              setOpenState(OPEN_STATE.OPEN)
            }}
            className={classes.date}
          >
            {formatVal({ type: field.field_type, value, inputOnly })}
          </div>
        )}
      </div>
    </div>
  )
}

export default DateInput
